import type { SupabaseClient } from "@supabase/supabase-js";
import { refundBounty as rpcRefundBounty } from "@/lib/supabase/rpc";

// ==================== Dashboard ====================

interface DashboardStats {
  totalUsers: number;
  totalBounties: number;
  pendingVerifications: number;
  totalTransactions: number;
}

interface RecentUser {
  id: string;
  nickname: string;
  email: string | null;
  isVerified: boolean;
  role: string;
  createdAt: string;
}

interface RecentBounty {
  id: string;
  title: string;
  status: string;
  rewardBbp: string;
  publisherNickname: string | null;
  createdAt: string;
}

export async function getDashboardStats(
  supabase: SupabaseClient,
): Promise<DashboardStats> {
  const [users, bounties, verifications, transactions] = await Promise.all([
    supabase.from("users").select("id", { count: "exact", head: true }),
    supabase.from("bounties").select("id", { count: "exact", head: true }),
    supabase
      .from("verify_applications")
      .select("id", { count: "exact", head: true })
      .eq("status", "pending"),
    supabase.from("point_transactions").select("id", { count: "exact", head: true }),
  ]);

  return {
    totalUsers: users.count ?? 0,
    totalBounties: bounties.count ?? 0,
    pendingVerifications: verifications.count ?? 0,
    totalTransactions: transactions.count ?? 0,
  };
}

export async function getRecentUsers(
  supabase: SupabaseClient,
  limit = 5,
): Promise<RecentUser[]> {
  const result = await supabase
    .from("users")
    .select("id,nickname,email,is_verified,role,created_at")
    .order("created_at", { ascending: false })
    .limit(limit);

  if (result.error || !result.data) return [];

  return result.data.map((u: Record<string, unknown>) => ({
    id: u.id as string,
    nickname: u.nickname as string,
    email: (u.email as string) ?? null,
    isVerified: u.is_verified as boolean,
    role: (u.role as string) ?? "user",
    createdAt: u.created_at as string,
  }));
}

export async function getRecentBounties(
  supabase: SupabaseClient,
  limit = 5,
): Promise<RecentBounty[]> {
  const result = await supabase
    .from("bounties")
    .select(
      "id,title,status,reward_usdc,created_at,publisher:users!bounties_publisher_id_fkey(nickname)",
    )
    .order("created_at", { ascending: false })
    .limit(limit);

  if (result.error || !result.data) return [];

  return result.data.map((r: Record<string, unknown>) => ({
    id: r.id as string,
    title: r.title as string,
    status: r.status as string,
    rewardBbp: String(r.reward_usdc ?? 0),
    publisherNickname:
      ((r.publisher as Record<string, unknown> | null)?.nickname as string) ?? null,
    createdAt: r.created_at as string,
  }));
}

// ==================== Users ====================

interface AdminUser {
  id: string;
  nickname: string;
  email: string | null;
  isVerified: boolean;
  role: string;
  level: number;
  points: number;
  direction: string | null;
  school: string | null;
  createdAt: string;
}

export async function listUsers(
  supabase: SupabaseClient,
  options: { page?: number; limit?: number; search?: string } = {},
): Promise<{ users: AdminUser[]; total: number }> {
  const { page = 1, limit = 20, search } = options;
  const offset = (page - 1) * limit;

  let query = supabase
    .from("users")
    .select("id,nickname,email,is_verified,role,level,points,direction,school,created_at", { count: "exact" })
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);

  if (search) {
    const sanitized = search.replace(/[%_\\]/g, "\\$&");
    query = query.or(
      `nickname.ilike.%${sanitized}%,email.ilike.%${sanitized}%`,
    );
  }

  const result = await query;

  if (result.error) return { users: [], total: 0 };

  const users = (result.data ?? []).map((u: Record<string, unknown>) => ({
    id: u.id as string,
    nickname: u.nickname as string,
    email: (u.email as string) ?? null,
    isVerified: u.is_verified as boolean,
    role: (u.role as string) ?? "user",
    level: (u.level as number) ?? 1,
    points: (u.points as number) ?? 0,
    direction: (u.direction as string) ?? null,
    school: (u.school as string) ?? null,
    createdAt: u.created_at as string,
  }));

  return { users, total: result.count ?? 0 };
}

export async function updateUserRole(
  supabase: SupabaseClient,
  userId: string,
  role: string,
): Promise<void> {
  const result = await supabase
    .from("users")
    .update({ role })
    .eq("id", userId);

  if (result.error) throw new Error(result.error.message);
}

export async function updateUserVerified(
  supabase: SupabaseClient,
  userId: string,
  isVerified: boolean,
): Promise<void> {
  const update: Record<string, unknown> = { is_verified: isVerified };
  if (isVerified) {
    update.verified_at = new Date().toISOString();
  } else {
    update.verified_at = null;
  }

  const result = await supabase
    .from("users")
    .update(update)
    .eq("id", userId);

  if (result.error) throw new Error(result.error.message);
}

// ==================== Verifications ====================

interface VerificationApplication {
  id: string;
  userId: string;
  userNickname: string;
  verifyType: string;
  evidenceUrl: string;
  status: string;
  reviewerId: string | null;
  reviewNote: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

export async function listVerifications(
  supabase: SupabaseClient,
  options: { page?: number; limit?: number; status?: string } = {},
): Promise<{ applications: VerificationApplication[]; total: number }> {
  const { page = 1, limit = 20, status } = options;
  const offset = (page - 1) * limit;

  let query = supabase
    .from("verify_applications")
    .select(
      "id,user_id,verify_type,evidence_url,status,reviewer_id,review_note,created_at,reviewed_at,user:users!verify_applications_user_id_fkey(nickname)",
      { count: "exact" },
    )
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);

  if (status) {
    query = query.eq("status", status);
  }

  const result = await query;
  if (result.error) return { applications: [], total: 0 };

  const applications = (result.data ?? []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    userId: r.user_id as string,
    userNickname:
      ((r.user as Record<string, unknown> | null)?.nickname as string) ?? "未知用户",
    verifyType: r.verify_type as string,
    evidenceUrl: r.evidence_url as string,
    status: r.status as string,
    reviewerId: (r.reviewer_id as string) ?? null,
    reviewNote: (r.review_note as string) ?? null,
    createdAt: r.created_at as string,
    reviewedAt: (r.reviewed_at as string) ?? null,
  }));

  return { applications, total: result.count ?? 0 };
}

export async function approveVerification(
  supabase: SupabaseClient,
  applicationId: string,
  reviewerId: string,
  note?: string,
): Promise<void> {
  const result = await supabase
    .from("verify_applications")
    .update({
      status: "approved",
      reviewer_id: reviewerId,
      review_note: note ?? null,
      reviewed_at: new Date().toISOString(),
    })
    .eq("id", applicationId);

  if (result.error) throw new Error(result.error.message);

  // Also update the user's is_verified
  const app = await supabase
    .from("verify_applications")
    .select("user_id")
    .eq("id", applicationId)
    .single();

  if (app.data) {
    await updateUserVerified(supabase, app.data.user_id, true);
  }
}

export async function rejectVerification(
  supabase: SupabaseClient,
  applicationId: string,
  reviewerId: string,
  note: string,
): Promise<void> {
  const result = await supabase
    .from("verify_applications")
    .update({
      status: "rejected",
      reviewer_id: reviewerId,
      review_note: note,
      reviewed_at: new Date().toISOString(),
    })
    .eq("id", applicationId);

  if (result.error) throw new Error(result.error.message);
}

// ==================== Bounties ====================

interface AdminBounty {
  id: string;
  title: string;
  status: string;
  rewardBbp: string;
  category: string | null;
  publisherNickname: string | null;
  claimedByNickname: string | null;
  createdAt: string;
}

export async function listBounties(
  supabase: SupabaseClient,
  options: { page?: number; limit?: number; status?: string } = {},
): Promise<{ bounties: AdminBounty[]; total: number }> {
  const { page = 1, limit = 20, status } = options;
  const offset = (page - 1) * limit;

  let query = supabase
    .from("bounties")
    .select(
      "id,title,status,reward_usdc,category,created_at,publisher:users!bounties_publisher_id_fkey(nickname),claimer:users!bounties_claimed_by_fkey(nickname)",
      { count: "exact" },
    )
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);

  if (status) {
    query = query.eq("status", status);
  }

  const result = await query;
  if (result.error) return { bounties: [], total: 0 };

  const bounties = (result.data ?? []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    title: r.title as string,
    status: r.status as string,
    rewardBbp: r.reward_usdc as string,
    category: (r.category as string) ?? null,
    publisherNickname:
      ((r.publisher as Record<string, unknown> | null)?.nickname as string) ?? null,
    claimedByNickname:
      ((r.claimer as Record<string, unknown> | null)?.nickname as string) ?? null,
    createdAt: r.created_at as string,
  }));

  return { bounties, total: result.count ?? 0 };
}

export async function adminCancelBounty(
  supabase: SupabaseClient,
  bountyId: string,
  reason: string,
): Promise<void> {
  const result = await supabase
    .from("bounties")
    .update({
      status: "cancelled",
      cancel_reason: reason,
      cancelled_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq("id", bountyId);

  if (result.error) throw new Error(result.error.message);

  // 退款：解冻发布方被冻结的 BBP
  try {
    await rpcRefundBounty(supabase, bountyId);
  } catch {
    // 退款失败不阻塞取消操作，但应该记录日志
    console.warn(`adminCancelBounty: BBP 退款失败 bounty=${bountyId}`);
  }
}

// ==================== User Detail ====================

interface UserBounty {
  id: string;
  title: string;
  status: string;
  rewardBbp: string;
  role: "publisher" | "claimer";
  createdAt: string;
}

interface UserConnection {
  id: string;
  targetNickname: string;
  status: string;
  createdAt: string;
}

export interface UserDetail {
  id: string;
  nickname: string;
  email: string | null;
  bio: string | null;
  school: string | null;
  company: string | null;
  direction: string | null;
  isVerified: boolean;
  role: string;
  level: number;
  points: number;
  createdAt: string;
  privyWalletAddress: string | null;
  bounties: UserBounty[];
  connections: UserConnection[];
  skills: string[];
}

export async function getUserDetail(
  supabase: SupabaseClient,
  userId: string,
): Promise<UserDetail | null> {
  // Get user basic info
  const userResult = await supabase
    .from("users")
    .select("id,nickname,email,bio,school,company,direction,is_verified,role,level,points,created_at")
    .eq("id", userId)
    .single();

  if (userResult.error || !userResult.data) return null;

  // Get user skills
  const skillsResult = await supabase
    .from("user_skills")
    .select("skill_name")
    .eq("user_id", userId);

  // Get bounties where user is publisher
  const publishedBounties = await supabase
    .from("bounties")
    .select("id,title,status,reward_usdc,created_at")
    .eq("publisher_id", userId)
    .order("created_at", { ascending: false })
    .limit(10);

  // Get bounties where user is claimer
  const claimedBounties = await supabase
    .from("bounties")
    .select("id,title,status,reward_usdc,created_at")
    .eq("claimed_by", userId)
    .order("created_at", { ascending: false })
    .limit(10);

  // Get connections
  const connectionsResult = await supabase
    .from("connections")
    .select("id,status,created_at,target_user:users!connections_target_user_id_fkey(nickname)")
    .eq("requester_id", userId)
    .order("created_at", { ascending: false })
    .limit(10);

  const user = userResult.data;
  const skills = (skillsResult.data ?? []).map((s: { skill_name: string }) => s.skill_name);

  const bounties: UserBounty[] = [
    ...(publishedBounties.data ?? []).map((b: Record<string, unknown>) => ({
      id: b.id as string,
      title: b.title as string,
      status: b.status as string,
      rewardBbp: b.reward_usdc as string,
      role: "publisher" as const,
      createdAt: b.created_at as string,
    })),
    ...(claimedBounties.data ?? []).map((b: Record<string, unknown>) => ({
      id: b.id as string,
      title: b.title as string,
      status: b.status as string,
      rewardBbp: b.reward_usdc as string,
      role: "claimer" as const,
      createdAt: b.created_at as string,
    })),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 10);

  const connections = (connectionsResult.data ?? []).map((c: Record<string, unknown>) => ({
    id: c.id as string,
    targetNickname: ((c.target_user as Record<string, unknown> | null)?.nickname as string) ?? "未知用户",
    status: c.status as string,
    createdAt: c.created_at as string,
  }));

  return {
    id: user.id as string,
    nickname: user.nickname as string,
    email: (user.email as string) ?? null,
    bio: (user.bio as string) ?? null,
    school: (user.school as string) ?? null,
    company: (user.company as string) ?? null,
    direction: (user.direction as string) ?? null,
    isVerified: user.is_verified as boolean,
    role: (user.role as string) ?? "user",
    level: (user.level as number) ?? 1,
    points: (user.points as number) ?? 0,
    createdAt: user.created_at as string,
    privyWalletAddress: null,
    bounties,
    connections,
    skills,
  };
}

// ==================== Bounty Detail ====================

interface BountyDeliverable {
  id: string;
  content: string;
  attachmentUrl: string | null;
  submittedAt: string;
}

export interface BountyDetail extends AdminBounty {
  description: string | null;
  publisherId: string;
  claimedBy: string | null;
  deadline: string | null;
  category: string | null;
  techTags: string[];
  escrowContractAddress: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  deliverables: BountyDeliverable[];
}

export async function getBountyDetail(
  supabase: SupabaseClient,
  bountyId: string,
): Promise<BountyDetail | null> {
  const result = await supabase
    .from("bounties")
    .select(
      `id,title,description,status,reward_usdc,category,tech_tags,created_at,deadline,
       publisher_id,publisher:users!bounties_publisher_id_fkey(nickname),
       claimed_by,claimer:users!bounties_claimed_by_fkey(nickname),
       cancelled_at,cancel_reason`,
    )
    .eq("id", bountyId)
    .single();

  if (result.error || !result.data) return null;

  const bounty = result.data as Record<string, unknown>;

  // Get deliverables
  const deliverablesResult = await supabase
    .from("deliverables")
    .select("id,content,attachment_url,submitted_at")
    .eq("bounty_id", bountyId)
    .order("submitted_at", { ascending: false });

  const deliverables = (deliverablesResult.data ?? []).map((d: Record<string, unknown>) => ({
    id: d.id as string,
    content: d.content as string,
    attachmentUrl: (d.attachment_url as string) ?? null,
    submittedAt: d.submitted_at as string,
  }));

  return {
    id: bounty.id as string,
    title: bounty.title as string,
    status: bounty.status as string,
    rewardBbp: String(bounty.reward_usdc ?? 0),
    category: (bounty.category as string) ?? null,
    publisherId: bounty.publisher_id as string,
    publisherNickname: ((bounty.publisher as Record<string, unknown> | null)?.nickname as string) ?? null,
    claimedBy: (bounty.claimed_by as string) ?? null,
    claimedByNickname: ((bounty.claimer as Record<string, unknown> | null)?.nickname as string) ?? null,
    createdAt: bounty.created_at as string,
    description: (bounty.description as string) ?? null,
    deadline: (bounty.deadline as string) ?? null,
    techTags: Array.isArray(bounty.tech_tags)
      ? (bounty.tech_tags as string[])
      : typeof bounty.tech_tags === "string" && bounty.tech_tags
        ? (bounty.tech_tags as string).split(",").map((t) => t.trim()).filter(Boolean)
        : [],
    escrowContractAddress: null,
    cancelledAt: (bounty.cancelled_at as string) ?? null,
    cancelReason: (bounty.cancel_reason as string) ?? null,
    deliverables,
  };
}

// ==================== Admin Logs ====================

interface AdminLog {
  id: string;
  adminId: string;
  adminNickname: string;
  action: string;
  targetType: string;
  targetId: string;
  details: string | null;
  createdAt: string;
}

export async function listAdminLogs(
  supabase: SupabaseClient,
  options: { page?: number; limit?: number } = {},
): Promise<{ logs: AdminLog[]; total: number }> {
  const { page = 1, limit = 20 } = options;
  const offset = (page - 1) * limit;

  const result = await supabase
    .from("admin_logs")
    .select(
      "id,admin_id,action,target_type,target_id,details,created_at,admin:users!admin_logs_admin_id_fkey(nickname)",
      { count: "exact" },
    )
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);

  if (result.error) return { logs: [], total: 0 };

  const logs = (result.data ?? []).map((l: Record<string, unknown>) => ({
    id: l.id as string,
    adminId: l.admin_id as string,
    adminNickname: ((l.admin as Record<string, unknown> | null)?.nickname as string) ?? "未知",
    action: l.action as string,
    targetType: l.target_type as string,
    targetId: l.target_id as string,
    details: (l.details as string) ?? null,
    createdAt: l.created_at as string,
  }));

  return { logs, total: result.count ?? 0 };
}

export async function createAdminLog(
  supabase: SupabaseClient,
  data: {
    adminId: string;
    action: string;
    targetType: string;
    targetId: string;
    details?: string;
  },
): Promise<void> {
  const result = await supabase.from("admin_logs").insert({
    admin_id: data.adminId,
    action: data.action,
    target_type: data.targetType,
    target_id: data.targetId,
    details: data.details ?? null,
  });

  if (result.error) throw new Error(result.error.message);
}

// ==================== System Notifications ====================

// ==================== Invitations (Admin) ====================

interface AdminInvitationCode {
  id: string;
  code: string;
  creatorId: string;
  creatorNickname: string | null;
  maxUses: number;
  currentUses: number;
  isActive: boolean;
  createdAt: string;
  expiresAt: string | null;
}

export async function listAllInvitationCodes(
  supabase: SupabaseClient,
  options: { page?: number; limit?: number } = {},
): Promise<{ codes: AdminInvitationCode[]; total: number }> {
  const { page = 1, limit = 20 } = options;
  const offset = (page - 1) * limit;

  const result = await supabase
    .from("invitation_codes")
    .select(
      "id,code,creator_id,max_uses,current_uses,is_active,created_at,expires_at,creator:users!invitation_codes_creator_id_fkey(nickname)",
      { count: "exact" },
    )
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);

  if (result.error) return { codes: [], total: 0 };

  const codes = (result.data ?? []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    code: r.code as string,
    creatorId: r.creator_id as string,
    creatorNickname: ((r.creator as Record<string, unknown> | null)?.nickname as string) ?? null,
    maxUses: Number(r.max_uses),
    currentUses: Number(r.current_uses),
    isActive: r.is_active as boolean,
    createdAt: r.created_at as string,
    expiresAt: (r.expires_at as string) ?? null,
  }));

  return { codes, total: result.count ?? 0 };
}

export async function toggleInvitationCode(
  supabase: SupabaseClient,
  codeId: string,
  isActive: boolean,
): Promise<void> {
  const result = await supabase
    .from("invitation_codes")
    .update({ is_active: isActive })
    .eq("id", codeId);

  if (result.error) throw new Error(result.error.message);
}

export async function getInvitationUsage(
  supabase: SupabaseClient,
  codeId: string,
): Promise<Array<{ id: string; userId: string; userNickname: string | null; usedAt: string }>> {
  const result = await supabase
    .from("invitation_usage")
    .select("id,user_id,used_at,user:users!invitation_usage_user_id_fkey(nickname)")
    .eq("code_id", codeId)
    .order("used_at", { ascending: false });

  if (result.error) return [];

  return (result.data ?? []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    userId: r.user_id as string,
    userNickname: ((r.user as Record<string, unknown> | null)?.nickname as string) ?? null,
    usedAt: r.used_at as string,
  }));
}

// ==================== BBP Balance (Admin) ====================

export async function adminAdjustBalance(
  supabase: SupabaseClient,
  userId: string,
  amount: number,
  note: string,
): Promise<void> {
  if (amount === 0) throw new Error("调整金额不能为 0");
  if (!note.trim()) throw new Error("备注不能为空");

  const MAX_RETRIES = 3;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    // Get or create balance
    const { data: existing } = await supabase
      .from("point_balances")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    let balanceBefore: number;
    let recordedUpdatedAt: string | null;

    if (!existing) {
      const { data: created, error: createError } = await supabase
        .from("point_balances")
        .insert({ user_id: userId })
        .select("*")
        .single();
      if (createError) throw new Error(`初始化余额失败: ${createError.message}`);
      balanceBefore = Number((created as Record<string, unknown>).balance ?? 0);
      recordedUpdatedAt = (created as Record<string, unknown>).updated_at as string ?? null;
    } else {
      balanceBefore = Number((existing as Record<string, unknown>).balance ?? 0);
      recordedUpdatedAt = (existing as Record<string, unknown>).updated_at as string ?? null;
    }

    const balanceAfter = balanceBefore + amount;
    if (balanceAfter < 0) throw new Error("调整后余额不能为负数");

    // CAS optimistic lock — only update if updated_at hasn't changed
    const newUpdatedAt = new Date().toISOString();
    let updateQuery = supabase
      .from("point_balances")
      .update({ balance: balanceAfter, updated_at: newUpdatedAt })
      .eq("user_id", userId);

    if (recordedUpdatedAt) {
      updateQuery = updateQuery.eq("updated_at", recordedUpdatedAt);
    }

    const { error: updateError, count } = await updateQuery;

    if (updateError) throw new Error(`更新余额失败: ${updateError.message}`);

    // CAS failed — concurrent modification detected, retry
    if (count === 0 && recordedUpdatedAt) {
      if (attempt < MAX_RETRIES - 1) continue;
      throw new Error("余额更新冲突，请重试");
    }

    // Create transaction record
    const type = amount > 0 ? "deposit" : "refund";
    const { error: txError } = await supabase
      .from("point_transactions")
      .insert({
        user_id: userId,
        type,
        amount: Math.abs(amount),
        balance_before: balanceBefore,
        balance_after: balanceAfter,
        frozen_before: 0,
        frozen_after: 0,
        description: `[管理员调整] ${note}`,
      });

    if (txError) throw new Error(`创建交易记录失败: ${txError.message}`);
    return;
  }
}

export async function getUserBbpBalance(
  supabase: SupabaseClient,
  userId: string,
): Promise<number> {
  const { data } = await supabase
    .from("point_balances")
    .select("balance")
    .eq("user_id", userId)
    .maybeSingle();
  return Number(data?.balance ?? 0);
}

// ==================== Admin User CRUD (Edge Function) ====================

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export interface CreateUserInput {
  email: string;
  password: string;
  nickname: string;
  phone?: string;
  school?: string;
  company?: string;
  direction?: string;
  bio?: string;
  level?: number;
  role?: string;
  is_verified?: boolean;
}

export interface UpdateProfileInput {
  nickname?: string;
  phone?: string;
  school?: string;
  company?: string;
  direction?: string;
  bio?: string;
}

async function callEdgeFunction(
  method: "POST" | "DELETE" | "PATCH",
  body: Record<string, unknown>,
): Promise<{ ok: boolean; data: Record<string, unknown>; error?: string }> {
  if (!SUPABASE_URL) {
    throw new Error("Supabase URL 未配置");
  }

  const { getBrowserSupabaseClient } = await import("@/lib/supabase/client");
  const supabase = getBrowserSupabaseClient();
  if (!supabase) {
    throw new Error("Supabase 客户端未初始化");
  }
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error("未登录或会话已过期");
  }

  const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-user-mgmt`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });

  let json: Record<string, unknown>;
  try {
    json = (await res.json()) as Record<string, unknown>;
  } catch {
    throw new Error(`请求失败 (${res.status})，服务端返回非 JSON 响应`);
  }

  if (!res.ok) {
    const msg = (json.error as string) ?? `请求失败 (${res.status})`;
    throw new Error(msg);
  }

  const inner = json.data as Record<string, unknown> | undefined;
  return { ok: true, data: inner ?? json };
}

export async function adminCreateUser(input: CreateUserInput): Promise<{ userId: string }> {
  const result = await callEdgeFunction("POST", {
    email: input.email,
    password: input.password,
    nickname: input.nickname,
    phone: input.phone,
    school: input.school,
    company: input.company,
    direction: input.direction,
    bio: input.bio,
    level: input.level,
    role: input.role,
    is_verified: input.is_verified,
  });

  return { userId: result.data.user_id as string };
}

export async function adminDeleteUser(userId: string): Promise<void> {
  await callEdgeFunction("DELETE", { user_id: userId });
}

export async function adminUpdateUserEmail(
  userId: string,
  newEmail: string,
): Promise<void> {
  await callEdgeFunction("PATCH", { user_id: userId, new_email: newEmail });
}

export async function adminFreezeUser(
  userId: string,
  reason?: string,
): Promise<void> {
  await callEdgeFunction("PATCH", {
    action: "freeze",
    user_id: userId,
    ...(reason ? { reason } : {}),
  });
}

export async function adminUnfreezeUser(
  userId: string,
  reason?: string,
): Promise<void> {
  await callEdgeFunction("PATCH", {
    action: "unfreeze",
    user_id: userId,
    ...(reason ? { reason } : {}),
  });
}

export async function adminResetPassword(
  userId: string,
  newPassword: string,
): Promise<void> {
  await callEdgeFunction("PATCH", {
    action: "reset_password",
    user_id: userId,
    new_password: newPassword,
  });
}

export async function updateUserProfile(
  supabase: SupabaseClient,
  userId: string,
  input: UpdateProfileInput,
): Promise<void> {
  const updateData: Record<string, unknown> = {};
  if (input.nickname !== undefined) updateData.nickname = input.nickname;
  if (input.phone !== undefined) updateData.phone = input.phone;
  if (input.school !== undefined) updateData.school = input.school;
  if (input.company !== undefined) updateData.company = input.company;
  if (input.direction !== undefined) updateData.direction = input.direction;
  if (input.bio !== undefined) updateData.bio = input.bio;

  if (Object.keys(updateData).length === 0) return;

  const result = await supabase
    .from("users")
    .update(updateData)
    .eq("id", userId);

  if (result.error) throw new Error(result.error.message);
}

// ==================== System Notifications ====================

export async function sendSystemNotification(
  supabase: SupabaseClient,
  data: {
    title: string;
    content: string;
    type: string;
    targetUserIds?: string[]; // if empty, send to all
  },
): Promise<void> {
  if (data.targetUserIds && data.targetUserIds.length > 0) {
    // Send to specific users
    const notifications = data.targetUserIds.map((userId) => ({
      user_id: userId,
      title: data.title,
      content: data.content,
      type: data.type,
    }));

    const result = await supabase.from("notifications").insert(notifications);
    if (result.error) throw new Error(result.error.message);
  } else {
    // Send to all users - use a simpler approach
    // Get all user IDs first
    const usersResult = await supabase
      .from("users")
      .select("id")
      .limit(1000);

    if (usersResult.data && usersResult.data.length > 0) {
      const notifications = usersResult.data.map((u: { id: string }) => ({
        user_id: u.id,
        title: data.title,
        content: data.content,
        type: data.type,
      }));

      const result = await supabase.from("notifications").insert(notifications);
      if (result.error) throw new Error(result.error.message);
    }
  }
}
